// "I switched" (architecture sprint-04 §6.1 F43). Never offered while paused (the provider checks).
// Preset availability and the exact-time range use the server-projected now, never the raw client
// clock against segmentStartedAt.
import { localToInstant, todayIn, type IanaZone } from '@shared/calendar';
import { MAX_DESCRIPTION_LENGTH, SWITCH_PRESET_MINUTES } from '@shared/entry-rules';
import { useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { DescriptionAutocomplete } from '@/components/entries/DescriptionAutocomplete';
import { timeInputToSeconds } from '@/components/entries/entry-form-model';
import { ProjectPicker } from '@/components/entries/ProjectPicker';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/cn';
import { serverNowMs } from '@/lib/timer-time';
import { useNowTick } from '@/lib/use-now';
import type { Me } from '@/types/me';
import type { SwitchBody, TimerSnapshot } from '@/types/timer';
import type { InlineErrorSink } from './TimerActionsProvider';

export const SWITCH_RANGE_MESSAGE = 'The switch time must be between when this activity started and now';
const MS_PER_MINUTE = 60_000;
const TICK_MS = 1000;

type When = { kind: 'preset'; minutes: number } | { kind: 'exact' };

/** Original pill button (also used by Team settings weekdays). */
export const PILL_CLASS =
  'rounded-lg border px-3 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-30';
export const PILL_SELECTED_CLASS = 'border-primary bg-primary text-primary-foreground';
export const PILL_UNSELECTED_CLASS = 'border-border text-muted-foreground hover:bg-accent hover:text-foreground';

const NEXT_KEYS = new Set(['ArrowRight', 'ArrowDown']);
const PREV_KEYS = new Set(['ArrowLeft', 'ArrowUp']);

function presetLabel(minutes: number): string {
  return minutes === 0 ? 'Just now' : `${minutes} min ago`;
}

interface SwitchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  snapshot: TimerSnapshot;
  me: Me;
  pending: boolean;
  onSubmit: (body: SwitchBody, inline: InlineErrorSink) => void;
}

export function SwitchDialog({ open, onOpenChange, snapshot, me, pending, onSubmit }: SwitchDialogProps): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>What are you doing now?</DialogTitle>
          {/* Kept for screen readers; visible text matches the original (A-4). */}
          <DialogDescription className="sr-only">Save what you were doing and start timing the new activity.</DialogDescription>
        </DialogHeader>
        {open && <SwitchForm snapshot={snapshot} me={me} pending={pending} onSubmit={onSubmit} onCancel={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

function SwitchForm({
  snapshot,
  me,
  pending,
  onSubmit,
  onCancel,
}: Omit<SwitchDialogProps, 'open' | 'onOpenChange'> & { onCancel: () => void }): JSX.Element {
  const tz: IanaZone = me.timezone;
  const defaultProject = me.preferences.defaultProject;
  const [projectId, setProjectId] = useState<string | null>(defaultProject?.status === 'active' ? defaultProject.id : null);
  const [description, setDescription] = useState('');
  const [when, setWhen] = useState<When>({ kind: 'preset', minutes: 0 });
  const [exact, setExact] = useState('');
  const [error, setError] = useState<string | null>(null);
  const pillRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const perfNow = useNowTick(TICK_MS);
  const nowMs = serverNowMs(snapshot, perfNow);
  const segmentStartMs = Date.parse(snapshot.timer.segmentStartedAt);

  const presetDisabled = (m: number): boolean => m > 0 && nowMs - m * MS_PER_MINUTE < segmentStartMs;

  function submit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    const base = { projectId, description: description.trim() === '' ? null : description.trim() };
    if (when.kind === 'preset') {
      onSubmit({ ...base, minutesAgo: when.minutes }, setError);
      return;
    }
    const second = timeInputToSeconds(exact);
    if (second === null) {
      setError(SWITCH_RANGE_MESSAGE);
      return;
    }
    const at = localToInstant(todayIn(tz), second, tz);
    if (at.getTime() < segmentStartMs || at.getTime() > nowMs) {
      setError(SWITCH_RANGE_MESSAGE);
      return;
    }
    onSubmit({ ...base, at: at.toISOString() }, setError);
  }

  // One pill per preset plus Exact time, as an ARIA radio group (arrow keys skip disabled pills).
  const options: Array<{ key: string; label: string; when: When; disabled: boolean }> = [
    ...SWITCH_PRESET_MINUTES.map((m) => ({ key: String(m), label: presetLabel(m), when: { kind: 'preset', minutes: m } as When, disabled: presetDisabled(m) })),
    { key: 'exact', label: 'Exact time', when: { kind: 'exact' } as When, disabled: false },
  ];
  const isSelected = (w: When): boolean => (w.kind === 'exact' ? when.kind === 'exact' : when.kind === 'preset' && when.minutes === w.minutes);
  const anyDisabled = options.some((o) => o.disabled);

  function onPillKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    const step = NEXT_KEYS.has(event.key) ? 1 : PREV_KEYS.has(event.key) ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    for (let i = 1; i <= options.length; i += 1) {
      const nextIndex = (index + step * i + options.length * options.length) % options.length;
      const next = options[nextIndex];
      if (next && !next.disabled) {
        setWhen(next.when);
        pillRefs.current[nextIndex]?.focus();
        return;
      }
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-3" noValidate>
      <div className="grid gap-1.5">
        <Label id="switch-project-label" htmlFor="switch-project">
          Project
        </Label>
        <ProjectPicker id="switch-project" aria-labelledby="switch-project-label switch-project-value" value={projectId} onChange={setProjectId} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="switch-description">Description</Label>
        <DescriptionAutocomplete
          id="switch-description"
          value={description}
          onChange={setDescription}
          maxLength={MAX_DESCRIPTION_LENGTH}
          placeholder="What are you working on?"
        />
      </div>
      <div role="radiogroup" aria-labelledby="switch-when-label">
        <span id="switch-when-label" className="block text-xs text-muted-foreground">
          When did you start this?
        </span>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {options.map((o, index) => {
            const selected = isSelected(o.when);
            return (
              <button
                key={o.key}
                ref={(el) => {
                  pillRefs.current[index] = el;
                }}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={selected ? 0 : -1}
                disabled={o.disabled}
                aria-describedby={o.disabled ? 'switch-when-note' : undefined}
                onClick={() => setWhen(o.when)}
                onKeyDown={(event) => onPillKeyDown(event, index)}
                className={cn(PILL_CLASS, selected ? PILL_SELECTED_CLASS : PILL_UNSELECTED_CLASS)}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        {anyDisabled && (
          <p id="switch-when-note" className="mt-1.5 text-xs text-muted-foreground">
            Greyed-out times are before this activity started.
          </p>
        )}
        {when.kind === 'exact' && (
          <Input aria-label="Switch time" type="time" step={60} className="mt-2 w-36 font-mono" value={exact} onChange={(e) => setExact(e.target.value)} />
        )}
      </div>
      {error && (
        <p role="alert" className="text-xs text-danger-fg">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button type="submit" className="flex-1" disabled={pending}>
          {pending ? 'Saving…' : 'Switch activity'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </DialogFooter>
    </form>
  );
}
